import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    CircularProgress,
    TextField,
    ListItem,
    List,
    Paper,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Fab,
    Grid,
    Card,
    CardContent,
    Divider,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Add as AddIcon,
    CloudUpload as UploadIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../config/api';
import {
    parseFreightDocument,
    FreightRecord,
    getFreightRecords,
    deleteFreightRecord
} from '../api/handleFreightRecords';
import { getAllCompanies, Company } from '../api/handleCompanies';
import { useAlert } from '../contexts/AlertContext';
import { useAccount } from '../contexts/AccountContext';

const FreightRecords = () => {
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();

    // Upload states
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<string | null>(null);
    const [jobId, setJobId] = useState<string | null>(null);
    const [supabaseFilePath, setSupabaseFilePath] = useState<string>('');
    const [polling, setPolling] = useState<NodeJS.Timeout | null>(null);
    const [results, setResults] = useState<string[]>([]);
    const [parsedData, setParsedData] = useState<FreightRecord | null>(null);
    const [parseLoading, setParseLoading] = useState(false);
    const [parseError, setParseError] = useState<string | null>(null);

    // Records display states
    const [records, setRecords] = useState<FreightRecord[]>([]);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [recordsError, setRecordsError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRecord, setSelectedRecord] = useState<FreightRecord | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [uploadSectionOpen, setUploadSectionOpen] = useState(false);

    // Company states
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [companiesLoading, setCompaniesLoading] = useState(false);

    const { showAlert } = useAlert();
    const navigate = useNavigate();

    useEffect(() => {
        fetchRecords();
        fetchCompanies();
    }, [currentAccount, isImpersonating, impersonatedUser]);

    const fetchRecords = async () => {
        if (!currentAccount) return;

        setRecordsLoading(true);
        setRecordsError(null);
        try {
            const impersonationData = isImpersonating && impersonatedUser ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const data = await getFreightRecords(impersonationData);
            setRecords(data);
        } catch (error: any) {
            setRecordsError(error.message);
        } finally {
            setRecordsLoading(false);
        }
    };

    const fetchCompanies = async () => {
        if (!currentAccount) return;

        setCompaniesLoading(true);
        try {
            const impersonationData = isImpersonating && impersonatedUser ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const data = await getAllCompanies(impersonationData);
            setCompanies(data);
        } catch (error: any) {
            console.error('Error fetching companies:', error);
        } finally {
            setCompaniesLoading(false);
        }
    };

    const handleDeleteRecord = async (id: string) => {
        try {
            const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            await deleteFreightRecord(id, impersonationData);
            setRecords(records.filter(r => r.id !== id));
            showAlert('Record deleted successfully', 'success');
        } catch (error: any) {
            console.error('Delete error:', error);
            showAlert(error.message || 'Failed to delete record', 'error');
        }
    };

    const handleViewDetails = (record: FreightRecord) => {
        setSelectedRecord(record);
        setDetailsOpen(true);
    };

    const handleReviewClick = (record: FreightRecord) => {
        navigate(`/records/review/${record.id}`, {
            state: {
                unmatchedCompanies: record.unmatchedCompanies,
            },
        });
    };

    const handleAddFreightRecord = () => {
        navigate('/records/add');
    };

    const addLog = (message: string) => {
        setResults((prev) => [...prev, message]);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setFile(event.target.files[0]);
        }
    };

    const handleParseWithAI = async (ocrText: string, rawOcrData: any, filePathOverride?: string) => {
        setParseLoading(true);
        setParseError(null);

        try {
            // Find the selected invoicer company details
            const invoicerCompany = companies.find(c => c.id === selectedCompany);

            // Use filePathOverride if provided, otherwise fall back to state
            const currentFilePath = filePathOverride || supabaseFilePath;

            const result = await parseFreightDocument(ocrText, rawOcrData, [currentFilePath], invoicerCompany);
            setParsedData(result.parsedData);

            if (result.savedRecord) {
                addLog('✅ Data parsed and saved to database successfully!');

                // Create complete record data by combining parsed data with saved record info
                const completeRecord = {
                    ...result.parsedData, // Enhanced parsed data with company IDs
                    id: result.savedRecord.id, // Ensure we have the database ID
                    createdAt: result.savedRecord.createdAt,
                    updatedAt: result.savedRecord.updatedAt,
                    attachmentUrls: result.savedRecord.attachmentUrls, // Include attachment URLs from saved record
                    rawOcrData: result.savedRecord.rawOcrData, // Include raw OCR data
                };

                // Check if there are unmatched companies that need attention
                if (result.unmatchedCompanies && result.unmatchedCompanies.length > 0) {
                    addLog(`⚠️ ${result.unmatchedCompanies.length} companies need attention - redirecting to review...`);

                    // Navigate to review with the record ID
                    setTimeout(() => {
                        navigate(`/records/review/${completeRecord.id}`, {
                            state: {
                                unmatchedCompanies: result.unmatchedCompanies,
                            },
                        });
                    }, 1500);
                } else {
                    addLog('✅ All companies matched successfully!');

                    // Navigate to review with the record ID
                    setTimeout(() => {
                        navigate(`/records/review/${completeRecord.id}`);
                    }, 2000);
                }

                // Refresh records list
                fetchRecords();
            } else {
                addLog('✅ Data parsed successfully!');

                // If not saved but parsed, still check for unmatched companies
                if (result.unmatchedCompanies && result.unmatchedCompanies.length > 0) {
                    addLog(`⚠️ ${result.unmatchedCompanies.length} companies need attention`);
                }
            }
        } catch (error: any) {
            setParseError(error.message);
            addLog(`❌ Parsing failed: ${error.message}`);
            showAlert(error.message || 'Failed to parse document', 'error');
        } finally {
            setParseLoading(false);
        }
    };

    const startPolling = (jobId: string, filePathForPolling?: string) => {
        addLog("📄 File uploaded to Supabase storage successfully!");
        addLog("🔄 Processing document with AWS Textract...");
        const interval = setInterval(async () => {
            try {
                const res = await axios.get(apiEndpoints.results(jobId));

                if (res.data.JobStatus === 'SUCCEEDED') {
                    clearInterval(interval);
                    setPolling(null);

                    // Extract text from Textract blocks
                    const blocks = res.data.Blocks || [];

                    const lines = blocks
                        .filter((b: any) => b.BlockType === 'LINE')
                        .map((b: any) => b.Text)
                        .join('\n');

                    addLog("✅ OCR text extracted successfully");
                    addLog("🤖 Parsing with AI...");

                    // Create a lightweight summary instead of sending all raw data
                    const ocrSummary = {
                        jobId: res.data.JobId,
                        jobStatus: res.data.JobStatus,
                        documentMetadata: res.data.DocumentMetadata,
                        totalBlocks: blocks.length,
                        processedAt: new Date().toISOString()
                    };

                    // Auto-parse with AI - pass the file path directly
                    await handleParseWithAI(lines, ocrSummary, filePathForPolling);

                    setLoading(false);
                } else if (res.data.JobStatus === 'FAILED') {
                    clearInterval(interval);
                    setPolling(null);
                    addLog('❌ Textract job failed.');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Polling error', error);
                clearInterval(interval);
                setPolling(null);
                setLoading(false);
            }
        }, 5000); // poll every 5 seconds

        setPolling(interval);
    };

    const handleUpload = async () => {
        if (!file || !selectedCompany) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('companyId', selectedCompany);

        setLoading(true);
        setResponse(null);
        setParsedData(null);
        setParseError(null);

        try {
            addLog("📤 Uploading file to Supabase storage...");
            const res = await axios.post(apiEndpoints.upload, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            addLog("✅ File uploaded successfully!");
            setResponse(JSON.stringify(res.data, null, 2));

            const { supabaseFilePath, jobId } = res.data;
            setJobId(jobId);
            setSupabaseFilePath(supabaseFilePath); // Update to use Supabase file path

            // Start polling for Textract results - pass the file path directly
            startPolling(jobId, supabaseFilePath);
        } catch (error: any) {
            addLog("❌ Upload failed: " + (error?.response?.data?.error || error.message));
            setResponse(error?.response?.data || 'Upload failed');
            setLoading(false);
        }
    };

    const formatCurrency = (value: number | null) => {
        if (value === null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';

        try {
            const date = new Date(dateString);
            // Check if the date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString();
        } catch (error) {
            console.warn('Error formatting date:', dateString, error);
            return 'Invalid Date';
        }
    };

    // Format date as MM.DD.YY for CSV export (matching user's spreadsheet format)
    const formatDateForCSV = (dateString: string | null | undefined): string => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            return `${month}.${day}.${year}`;
        } catch {
            return '';
        }
    };

    // Format currency for CSV export (with $ and commas)
    const formatCurrencyForCSV = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(value);
    };

    // Export records to CSV in user's spreadsheet format
    const exportToCSV = () => {
        const headers = [
            'Outlet',
            'Carrier',
            'Del Date',
            'Del Month',
            'Ticket #',
            'BOL #',
            'Supplier',
            'Net Tons',
            'GP',
            'FRT Cost',
            'Net GP',
            'Driver',
            'Dust Cost',
            'PAID'
        ];

        const rows = records.map(r => [
            r.outlet || '',
            r.carrier || '',
            formatDateForCSV(r.deliveryDate),
            r.deliveryMonth || '',
            r.ticketNumber || '',
            r.bolNumber || '',
            r.supplier || '',
            r.netTons?.toString() || '',
            formatCurrencyForCSV(r.grossPrice),
            formatCurrencyForCSV(r.freightCost),
            formatCurrencyForCSV(r.netGrossPrice),
            r.driver || '',
            formatCurrencyForCSV(r.dustCost),
            r.paidStatus ? 'Yes' : ''
        ]);

        // Escape CSV values (handle commas and quotes)
        const escapeCSV = (value: string): string => {
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        };

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(escapeCSV).join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        
        // Generate filename with current date
        const now = new Date();
        const month = now.toLocaleString('default', { month: 'long' });
        const year = now.getFullYear();
        link.setAttribute('download', `${month} ${year} Freight Records.csv`);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showAlert(`Exported ${records.length} records to CSV`, 'success');
    };

    const paginatedRecords = records.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const viewAllRecordData = async () => {
        let records = await getFreightRecords();
        console.log(records);
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Freight Records
                </Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={records.length === 0}
                    >
                        Export CSV
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadSectionOpen(!uploadSectionOpen)}
                    >
                        Upload Document
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddFreightRecord}
                    >
                        Add Record
                    </Button>
                </Box>
            </Box>

            {/* Upload Section */}
            {uploadSectionOpen && (
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Document OCR Processing
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Upload freight documents to Supabase storage and automatically extract data using AWS Textract + AI
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                💡 Select the company that sent this invoice/document (e.g., Heritage Freight for a carrier invoice)
                            </Typography>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Select Invoicing Company</InputLabel>
                                <Select
                                    value={selectedCompany}
                                    label="Select Invoicing Company"
                                    onChange={(e) => setSelectedCompany(e.target.value)}
                                    disabled={companiesLoading}
                                >
                                    {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            <Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {company.name}
                                                    {(company.isCarrier || company.isSupplier || company.isOutlet || company.isBillTo) && (
                                                        <Box component="span" sx={{ ml: 1, opacity: 0.6 }}>
                                                            ({[
                                                                company.isCarrier && 'Carrier',
                                                                company.isSupplier && 'Supplier',
                                                                company.isOutlet && 'Outlet',
                                                                company.isBillTo && 'Bill-To'
                                                            ].filter(Boolean).join(', ')})
                                                        </Box>
                                                    )}
                                                </Box>
                                                {company.alias && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        aka {company.alias}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ my: 2 }}>
                            <Button variant="outlined" component="label">
                                Choose File
                                <input type="file" hidden onChange={handleFileChange} />
                            </Button>
                            <Typography variant="body2" sx={{ ml: 2 }} display="inline">
                                {file?.name || 'No file selected'}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={!file || !selectedCompany || loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                        >
                            {loading ? 'Processing...' : 'Upload to Process'}
                        </Button>

                        {!selectedCompany && file && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                Please select the invoicing company before uploading
                            </Typography>
                        )}

                        {results.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Processing Log:
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                                    <List dense>
                                        {results.map((log, idx) => (
                                            <ListItem key={idx} sx={{ py: 0 }}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {log}
                                                </Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Box>
                        )}

                        {parsedData && (
                            <Box sx={{ mt: 3 }}>
                                <Alert
                                    severity={parsedData.needsReview ? "warning" : "success"}
                                    sx={{ mb: 2 }}
                                >
                                    {parsedData.needsReview
                                        ? `Document parsed but needs review (Confidence: ${(parsedData.confidenceScore * 100).toFixed(1)}%)`
                                        : 'Document parsed successfully!'
                                    }
                                </Alert>
                            </Box>
                        )}

                        {parseError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Parsing Error: {parseError}
                            </Alert>
                        )}

                        {parseLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                <Typography>Parsing with AI...</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Records Table */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            All Freight Records ({records.length})
                        </Typography>
                        {recordsLoading && <CircularProgress size={24} />}
                    </Box>

                    {recordsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {recordsError}
                        </Alert>
                    )}

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Outlet</TableCell>
                                    <TableCell>Carrier</TableCell>
                                    <TableCell>Del Date</TableCell>
                                    <TableCell>Ticket #</TableCell>
                                    <TableCell>BOL #</TableCell>
                                    <TableCell>Supplier</TableCell>
                                    <TableCell>Net Tons</TableCell>
                                    <TableCell>GP</TableCell>
                                    <TableCell>FRT Cost</TableCell>
                                    <TableCell>Net GP</TableCell>
                                    <TableCell>Driver</TableCell>
                                    <TableCell>PAID</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRecords.map((record) => (
                                    <TableRow key={record.id} hover>
                                        <TableCell>{record.outlet || '-'}</TableCell>
                                        <TableCell>{record.carrier || '-'}</TableCell>
                                        <TableCell>{formatDate(record.deliveryDate)}</TableCell>
                                        <TableCell>{record.ticketNumber || '-'}</TableCell>
                                        <TableCell>{record.bolNumber || '-'}</TableCell>
                                        <TableCell>{record.supplier || '-'}</TableCell>
                                        <TableCell>{record.netTons || '-'}</TableCell>
                                        <TableCell>{formatCurrency(record.grossPrice)}</TableCell>
                                        <TableCell>{formatCurrency(record.freightCost)}</TableCell>
                                        <TableCell>{formatCurrency(record.netGrossPrice)}</TableCell>
                                        <TableCell>{record.driver || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={record.paidStatus ? <CheckCircleIcon /> : <CancelIcon />}
                                                label={record.paidStatus ? 'Paid' : 'Unpaid'}
                                                color={record.paidStatus ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {record.needsReview && (
                                                    <Tooltip title="Needs Review">
                                                        <IconButton size="small" color="warning">
                                                            <WarningIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleReviewClick(record)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(record)}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteRecord(record.id!)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={records.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                    />
                </CardContent>
            </Card>

            {/* Details Modal */}
            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Freight Record Details
                    {selectedRecord?.needsReview && (
                        <Chip
                            icon={<WarningIcon />}
                            label={`Needs Review (${(selectedRecord.confidenceScore * 100).toFixed(1)}% confidence)`}
                            color="warning"
                            size="small"
                            sx={{ ml: 2 }}
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Outlet</Typography>
                                    <Typography variant="body1">{selectedRecord.outlet || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Carrier</Typography>
                                    <Typography variant="body1">{selectedRecord.carrier || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Del Date</Typography>
                                    <Typography variant="body1">{formatDate(selectedRecord.deliveryDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Ticket #</Typography>
                                    <Typography variant="body1">{selectedRecord.ticketNumber || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">BOL #</Typography>
                                    <Typography variant="body1">{selectedRecord.bolNumber || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Supplier</Typography>
                                    <Typography variant="body1">{selectedRecord.supplier || '-'}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, mb: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Net Tons</Typography>
                                    <Typography variant="body1">{selectedRecord.netTons || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">GP</Typography>
                                    <Typography variant="body1">{formatCurrency(selectedRecord.grossPrice)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">FRT Cost</Typography>
                                    <Typography variant="body1">{formatCurrency(selectedRecord.freightCost)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Net GP</Typography>
                                    <Typography variant="body1">{formatCurrency(selectedRecord.netGrossPrice)}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Driver</Typography>
                                    <Typography variant="body1">{selectedRecord.driver || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Dust Cost</Typography>
                                    <Typography variant="body1">{formatCurrency(selectedRecord.dustCost)}</Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Del Month</Typography>
                                    <Typography variant="body1">{selectedRecord.deliveryMonth || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Files</Typography>
                                    <Typography variant="body1">{selectedRecord.attachmentUrls?.length ? `${selectedRecord.attachmentUrls.length} file(s)` : '-'}</Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="body2" color="text.secondary">PAID Status</Typography>
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    icon={selectedRecord.paidStatus ? <CheckCircleIcon /> : <CancelIcon />}
                                    label={selectedRecord.paidStatus ? 'Paid' : 'Unpaid'}
                                    color={selectedRecord.paidStatus ? 'success' : 'default'}
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    label={selectedRecord.vendorType}
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default FreightRecords; 